/**
 * @fileoverview OnboardingModal - Простое модальное окно с инструкциями
 * @description Показывается один раз при первом входе, описывает шаги настройки
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2 } from 'lucide-react';

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto p-6">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-2xl font-bold">
            Добро пожаловать в ArendaPro+! 👋
          </DialogTitle>
          <DialogDescription className="text-base">
            Давайте настроим систему за 5 простых шагов
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 px-2">
          {/* Шаг 1 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold">
                1
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Системные настройки</h3>
              <p className="text-gray-600">
                Перейдите в <strong>Настройки</strong> (кнопка профиля → Настройки) и укажите название вашей компании, 
                контактные данные и региональные параметры (валюта, часовой пояс, налоговая ставка).
              </p>
            </div>
          </div>

          {/* Шаг 2 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold">
                2
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Пункты аренды</h3>
              <p className="text-gray-600">
                В разделе <strong>Пункты</strong> создайте пункты проката — это ваши офисы, склады или точки выдачи оборудования. 
                По умолчанию создан один пункт, вы можете его отредактировать.
              </p>
            </div>
          </div>

          {/* Шаг 3 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold">
                3
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Справочники (Теги)</h3>
              <p className="text-gray-600">
                В разделе <strong>Справочники</strong> находятся теги для фильтрации и поиска. 
                Система создала базовые теги автоматически, добавьте привычные для вас названия.
              </p>
            </div>
          </div>

          {/* Шаг 4 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold">
                4
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Категории</h3>
              <p className="text-gray-600">
                В разделе <strong>Категории</strong> добавьте категории для классификации вашего инвентаря, товаров и услуг.
              </p>
            </div>
          </div>

          {/* Шаг 5 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold">
                5
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Тарифы</h3>
              <p className="text-gray-600">
                В разделе <strong>Тарифы</strong> настройте тарифные планы для аренды. 
                Укажите цены и условия для каждого тарифа.
              </p>
            </div>
          </div>

          {/* Завершение */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-green-900 mb-1">Готово!</h4>
              <p className="text-green-700 text-sm">
                После настройки этих разделов вы сможете добавлять инвентарь, создавать клиентов и оформлять заказы. 
                Дополнительные настройки доступны в любое время.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
          <Button onClick={onClose} size="lg" className="px-8">
            Понятно, приступаю к настройке
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
